/**
 * Day-wise pledge interest (no minimum one-month charge).
 *
 * monthlyInterest = amount × rate / 100
 * dailyInterest   = monthlyInterest / 30
 * periodInterest  = dailyInterest × billableDays
 *
 * FINAL bank day-count — inclusive calendar days for every period:
 * - Period START date is included
 * - Period END / settlement date is included
 * - billableDays = calendarDaysBetween(start, end) + 1
 * - After settlement, NEXT period starts on settlementDate + 1 day
 *
 * Examples:
 * 07 Aug → 08 Aug = 2 days
 * 08 Aug → 08 Aug = 1 day
 * 09 Aug → 20 Aug = 12 days
 * 01 Aug → 04 Aug = 4 days
 */

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const toUtcDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
};

const calendarDaysBetween = (start, end) => {
  if (!start || !end) return 0;
  return Math.round((end - start) / MS_PER_DAY);
};

/** Inclusive calendar-day count: both start and end dates are billable. */
export const calculateBillableDays = (periodStart, periodEnd) => {
  const start = toUtcDate(periodStart);
  const end = toUtcDate(periodEnd);
  if (!start || !end || end < start) return 0;
  return calendarDaysBetween(start, end) + 1;
};

/** Next unpaid period begins the calendar day after the settlement date. */
export const nextPeriodStartAfterSettlement = (settlementDate) => {
  const d = toUtcDate(settlementDate);
  if (!d) return null;
  d.setUTCDate(d.getUTCDate() + 1);
  return d;
};

export const calculateMonthlyInterest = (pledgeAmount, interestRate) => {
  if (!pledgeAmount || !interestRate) return 0;
  return (Number(pledgeAmount) * Number(interestRate)) / 100;
};

export const calculateDailyInterest = (pledgeAmount, interestRate) => (
  calculateMonthlyInterest(pledgeAmount, interestRate) / 30
);

export const calculateInterestForDays = (pledgeAmount, interestRate, days) => {
  if (!days || days <= 0) return 0;
  return Math.round(calculateDailyInterest(pledgeAmount, interestRate) * days * 100) / 100;
};

/**
 * @param {number} pledgeAmount
 * @param {number} interestRate
 * @param {string|Date} pledgeDate
 * @param {string|Date|null} endDate
 * @param {{ lastSettlementDate?: string|Date, periodStart?: string|Date }} [options]
 */
export const calculatePledgeInterest = (
  pledgeAmount,
  interestRate,
  pledgeDate,
  endDate = null,
  options = {}
) => {
  if (!pledgeAmount || !interestRate || !pledgeDate) {
    return {
      totalInterest: 0,
      oneMonthInterest: 0,
      additionalDaysInterest: 0,
      totalDays: 0,
      months: 0,
      additionalDays: 0,
      monthlyInterest: 0,
      dailyInterest: 0,
      billableDays: 0
    };
  }

  let periodStart = options.periodStart || pledgeDate;
  if (options.lastSettlementDate) {
    const nextStart = nextPeriodStartAfterSettlement(options.lastSettlementDate);
    const pledgeStart = toUtcDate(pledgeDate);
    if (nextStart && (!pledgeStart || nextStart >= pledgeStart)) {
      periodStart = nextStart;
    }
  }

  const periodEnd = endDate || new Date();
  const billableDays = calculateBillableDays(periodStart, periodEnd);
  const monthlyInterest = calculateMonthlyInterest(pledgeAmount, interestRate);
  const dailyInterest = calculateDailyInterest(pledgeAmount, interestRate);
  const totalInterest = calculateInterestForDays(pledgeAmount, interestRate, billableDays);

  return {
    totalInterest,
    oneMonthInterest: Math.round(monthlyInterest * 100) / 100,
    additionalDaysInterest: 0,
    totalDays: billableDays,
    billableDays,
    accruedInclusiveDays: billableDays,
    months: 0,
    additionalDays: billableDays,
    monthlyInterest: Math.round(monthlyInterest * 100) / 100,
    dailyInterest: Math.round(dailyInterest * 10000) / 10000
  };
};

export const formatInterestBreakdown = (interestBreakdown) => {
  if (!interestBreakdown || !interestBreakdown.totalDays) {
    return {
      duration: '0 days',
      breakdown: 'No interest',
      summary: '₹0'
    };
  }

  const { totalDays, totalInterest, monthlyInterest, dailyInterest } = interestBreakdown;
  const duration = `${totalDays} day${totalDays !== 1 ? 's' : ''}`;
  const breakdown = `₹${Number(monthlyInterest || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })} / month ÷ 30 × ${totalDays} days`;

  return {
    duration,
    breakdown,
    summary: `₹${Number(totalInterest || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
    dailyInterest
  };
};
